import { Layer, type ObjectCategory } from "@common/constants";
import { makeGameObjectTemplate } from "@common/utils/gameObject";
import { Angle, Numeric } from "@common/utils/math";
import { type Timeout } from "@common/utils/misc";
import { type ObjectsNetData } from "@common/utils/objectsSerializations";
import { Vec, type Vector } from "@common/utils/vector";
import { Container } from "pixi.js";
import { Game } from "../game";
import { SoundManager, type GameSound, type SoundOptions } from "../managers/soundManager";
import { toPixiCoords } from "../utils/pixi";
import { CameraManager } from "../managers/cameraManager";
import { LagSmoothing } from "../managers/lagSmoothing";
import { LAG_SMOOTHING_TELEPORT_DISTANCE } from "../managers/lagSmoothingLogic";
import { getLayerContainer } from "@common/utils/layer";

export abstract class GameObject<Cat extends ObjectCategory = ObjectCategory> extends makeGameObjectTemplate() {
    damageable = false;
    destroyed = false;

    layer!: Layer;

    private _oldPosition?: Vector;
    private _lastPositionChange?: number;
    private _position = Vec(0, 0);
    private _positionManuallySet = false;
    /** where it was last drawn (lag smoothing eases on from there) */
    private _drawnPosition?: Vector;
    /** lag smoothing's easing time for the current move (ms); otherwise the time between the last two updates */
    private _positionWindow?: number;
    get position(): Vector { return this._position; }
    /** where it's drawn (game units: between its last two updates, while movement is smoothed), or where it is */
    get drawnPosition(): Vector { return this._drawnPosition ?? this._position; }
    set position(position: Vector) {
        if (this._positionManuallySet) {
            const drawn = this._drawnPosition;
            if (LagSmoothing.smoothing && drawn !== undefined) {
                // on from where it's drawn now (never back to where the last update put it), unless it's a teleport
                const teleport = Vec.squaredLen(Vec.sub(position, drawn)) > LAG_SMOOTHING_TELEPORT_DISTANCE ** 2;
                this._oldPosition = teleport ? Vec.clone(position) : drawn;
                this._positionWindow = LagSmoothing.window;
            } else {
                // just switched off: on from where it's drawn once more, so it doesn't hop
                this._oldPosition = this._positionWindow !== undefined && drawn !== undefined ? drawn : Vec.clone(this._position);
                this._positionWindow = undefined;
            }
        }
        this._positionManuallySet = true;

        this._lastPositionChange = Date.now();
        this._position = position;
        this._settled = false;
    }

    /** drawn where it is at the end of its last move, and nothing has moved it since (0.98.8) */
    private _settled = false;

    updateContainerPosition(): void {
        if (
            this.destroyed
            || this._oldPosition === undefined
            || this._lastPositionChange === undefined
        ) return;

        const t = Numeric.min(
            (Date.now() - this._lastPositionChange) / (this._positionWindow ?? Game.serverDt),
            1
        );
        // (0.98.8: come to rest where its last move ended, and drawn there already: nothing new to work out or make.
        // A thing lying still (the loot) made new vectors for the same place every frame)
        if (t >= 1 && this._settled) return;
        const drawn = Vec.lerp(this._oldPosition, this.position, t);
        this._drawnPosition = drawn;
        this.container.position = toPixiCoords(drawn);
        this._settled = t >= 1;
    }

    private _oldRotation?: number;
    private _lastRotationChange?: number;
    private _rotationManuallySet = false;
    private _rotation = 0;
    /** the angle it was last drawn at (lag smoothing turns on from there) */
    private _drawnRotation?: number;
    /** lag smoothing's easing time for the current turn (ms); otherwise the time between the last two updates */
    private _rotationWindow?: number;
    get rotation(): number { return this._rotation; }
    set rotation(rotation: number) {
        if (this._rotationManuallySet) {
            if (LagSmoothing.smoothing && this._drawnRotation !== undefined) {
                this._oldRotation = this._drawnRotation;
                this._rotationWindow = LagSmoothing.window;
            } else {
                this._oldRotation = this._rotationWindow !== undefined && this._drawnRotation !== undefined ? this._drawnRotation : this._rotation;
                this._rotationWindow = undefined;
            }
        }
        this._rotationManuallySet = true;

        this._lastRotationChange = Date.now();
        this._rotation = rotation;
    }

    updateContainerRotation(): void {
        if (
            this._oldRotation === undefined
            || this._lastRotationChange === undefined
        ) return;

        const drawn = Numeric.lerp(
            this._oldRotation,
            this._oldRotation + Angle.minimize(this._oldRotation, this._rotation),
            Numeric.min(
                (Date.now() - this._lastRotationChange) / (this._rotationWindow ?? Game.serverDt),
                1
            )
        );
        this._drawnRotation = drawn;
        this.container.rotation = drawn;
    }

    dead = false;

    readonly container = new Container();
    disguiseContainer?: Container;

    readonly timeouts = new Set<Timeout>();

    addTimeout(callback: () => void, delay?: number): Timeout {
        const timeout = Game.addTimeout(callback, delay);
        this.timeouts.add(timeout);
        return timeout;
    }

    constructor(readonly id: number) {
        super();
    }

    destroy(): void {
        this.destroyed = true;
        for (const timeout of this.timeouts) {
            timeout.kill();
        }
        this.container.destroy();
    }

    playSound(name: string, options?: Partial<Omit<SoundOptions, "position">>): GameSound {
        return SoundManager.play(name, {
            position: this.position,
            layer: this.layer,
            ...options
        });
    }

    abstract updateFromData(data: ObjectsNetData[Cat], isNew: boolean): void;

    layerContainer?: Container;
    layerContainerIndex?: number;
    readonly containers: Container[] = [this.container];
    updateLayer(forceUpdate = false): void {
        const oldContainer = this.layerContainer;
        const newContainer = CameraManager.getContainer(this.layer, this.layerContainerIndex);
        if (oldContainer === newContainer && !forceUpdate) return;

        this.layerContainer = newContainer;
        this.layerContainerIndex = getLayerContainer(this.layer, Game.layer);

        const conts = this.containers;
        if (this.isPlayer) {
            if (this.disguiseContainer === undefined) this.disguiseContainer = new Container();
            conts.push(this.disguiseContainer);
        }

        for (const container of conts) {
            oldContainer?.removeChild(container);
            newContainer.addChild(container);
        }
    }

    abstract update(): void;
    abstract updateInterpolation(): void;

    abstract updateDebugGraphics(): void;
}
